import { memo } from 'react';
import moment, { type Moment } from 'moment-timezone';
// Note that we are importing from @mui/x-date-pickers multiple times because this lib does not have any default exports on it's components
// this means our babel plugin can't auto split these components into multiple imports, so this is the best option for now.
import {
    DateTimePicker as MuiDatePicker,
    type DateTimePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { type Optional } from '@Types';
import { useFormContext, Controller } from 'FrontRoyalReactHookForm';
import { type DateOrTimeView } from '@mui/x-date-pickers/models';

type PickerProps = Optional<MuiDatePickerProps, 'onChange' | 'value'>;

type DatePickerProps = PickerProps & {
    name: string;
    timezone?: string;
    views?: DateOrTimeView[];
};

const DatePickerComponent = ({
    name,
    timezone = moment.tz.guess(),
    views = ['year', 'month', 'day', 'hours', 'minutes'],
    slotProps,
    ...rest
}: DatePickerProps) => {
    const { control, getFieldState } = useFormContext<{ [name: string]: Moment }>();
    const { error, isDirty } = getFieldState(name);

    return (
        <Controller
            control={control}
            name={name}
            defaultValue={moment.tz(timezone)}
            render={({ field }) => {
                const zoneAbbr = (
                    field.value?.isValid() ? moment.tz(field.value, timezone) : moment.tz(timezone)
                ).format('z');

                const mergedSlotProps = {
                    ...slotProps,
                    openPickerButton: {
                        'aria-label': 'change date',
                        style: { width: '40px' },
                        ...slotProps?.openPickerButton,
                    },
                    textField: {
                        fullWidth: true,
                        error: isDirty && !!error,
                        helperText: zoneAbbr,
                        // Strip the border the global .front-royal-form-container CSS leaks onto the
                        // x-date-pickers contenteditable field.
                        sx: { '& .MuiPickersSectionList-root': { border: 0, borderRadius: 0 } },
                        ...slotProps?.textField,
                    },
                    toolbar: { hidden: false, ...slotProps?.toolbar },
                };

                return (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <MuiDatePicker
                            format="YYYY/MM/DD h:mm A"
                            timezone={timezone}
                            views={views}
                            label={name}
                            value={field.value}
                            onChange={field.onChange}
                            {...rest}
                            slotProps={mergedSlotProps}
                        />
                    </LocalizationProvider>
                );
            }}
        />
    );
};

const compareFn = (prev: DatePickerProps, next: DatePickerProps) =>
    prev.label === next.label && prev.timezone === next.timezone;

export const DatePicker = memo(DatePickerComponent, compareFn) as typeof DatePickerComponent;

export default DatePicker;
